import { useEffect, useState } from 'react';

interface KeyboardViewport {
  /** Teclado virtual aberto (visualViewport encolheu além do threshold). */
  kbOpen: boolean;
  /** Altura útil da viewport com teclado aberto; null quando fechado. */
  vvHeight: number | null;
}

/**
 * Detecta o teclado virtual via visualViewport (NSS §5.3 do brief sem-scroll).
 *
 * iOS não encolhe o layout viewport (100dvh) quando o teclado sobe — só o
 * visualViewport. Sem isso, o CTA de uma tela de viewport fixa fica escondido
 * atrás do teclado. Comparar contra a altura capturada no mount funciona nos
 * dois mundos: no Android resizes-content os dois encolhem juntos e o kbOpen
 * também dispara.
 *
 * Threshold de 120px evita falso-positivo com barras dinâmicas do browser.
 *
 * `resetScroll: false` é obrigatório quando o hook monta em rota que rola o
 * documento (ex.: BottomNav em Wallet/Profile) — o resize do visualViewport
 * também dispara em colapso de URL bar, e zerar o scroll ali briga com o
 * usuário. O default true preserva o comportamento das telas NSS de Auth.
 */
export function useKeyboardViewport(
  threshold = 120,
  resetScroll = true
): KeyboardViewport {
  const [state, setState] = useState<KeyboardViewport>({
    kbOpen: false,
    vvHeight: null
  });

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const base = vv.height;
    const update = () => {
      // Pinch-zoom também encolhe vv.height (e o iOS Safari ignora
      // user-scalable=no) — só é teclado se a página não está ampliada.
      const kb = vv.scale <= 1.01 && vv.height < base - threshold;
      setState((prev) =>
        prev.kbOpen === kb && (!kb || prev.vvHeight === Math.round(vv.height))
          ? prev
          : { kbOpen: kb, vvHeight: kb ? Math.round(vv.height) : null }
      );
      // iOS pode ter panado a página no focus (reveal do input) ANTES deste
      // resize chegar — com o frame já colapsado pra vvHeight, o pan deixa a
      // tela "subida" (bug 2026-07-04 no passo de e-mail do Login). Zera o
      // scroll do documento; telas NSS nunca rolam o documento de propósito.
      if (resetScroll) {
        requestAnimationFrame(() => window.scrollTo(0, 0));
      }
    };
    vv.addEventListener('resize', update);
    return () => vv.removeEventListener('resize', update);
  }, [threshold, resetScroll]);

  return state;
}
